import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './StrategyModule.module.css';
import { Project } from '../../types';
import { Modal } from '../common/Modal/Modal';
import { Button } from '../common/Button/Button';
import { useCrud } from '../../hooks/useCrud';
import { strategyAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { StrategyDetail } from './StrategyDetail';
import {
  MegaphoneIcon,
  SpeechBubbleIcon,
  HandshakeIcon,
  GroupIcon,
  BallotIcon,
  MessageIcon,
  SirenIcon,
  CampaignIcon,
  EditPenIcon,
  SmartphoneIcon,
  NewspaperIcon,
  ClipboardIcon
} from '../common/Icons/ActionIcons';

interface StrategyModuleProps {
  project: Project;
}

interface Strategy {
  _id: string;
  name: string;
  description: string;
  type: 'communication' | 'engagement' | 'crisis' | 'campaign' | 'content' | 'social_media' | 'pr' | 'other';
  status: 'draft' | 'review' | 'approved' | 'active' | 'paused' | 'completed' | 'archived';
  objectives?: Array<{
    title: string;
    description: string;
    measurable?: string;
    deadline?: Date;
    completed?: boolean;
  }>;
  keyMessages?: Array<{
    message: string;
    audience?: string;
    channel?: string;
    priority?: 'low' | 'medium' | 'high';
  }>;
  channels?: string[];
  timeline?: {
    startDate?: Date;
    endDate?: Date;
  };
  budget?: {
    total?: number;
    allocated?: number;
    spent?: number;
    currency?: string;
    breakdown?: { category: string; amount: number; description: string }[];
    rationale?: string;
  };
  kpis?: { name: string; description?: string; target?: number; current?: number; unit?: string }[];
  risks?: { description: string; impact?: string; probability?: string; mitigation?: string }[];
  stakeholders?: { name: string; role?: string; influence?: string; interest?: string; engagementStrategy?: string }[];
  notes?: string;
  // SOM/MGA fields
  theoreticalBasis?: string;
  stakeholderMapping?: {
    actors?: { name: string; category?: string; position?: string; interest?: string; exchangeSpace?: string }[];
    interestMatrix?: string;
  };
  issueAnalysis?: {
    conflictPoints?: string[];
    gains?: string[];
  };
  coreMessage?: string;
  participationLevels?: { stakeholderGroup: string; level: string; reasoning?: string }[];
}

export const StrategyModule: React.FC<StrategyModuleProps> = ({ project }) => {
  const { user } = useAuth();
  const { '*': subPath } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    type: Strategy['type'];
    objectives: string;
    keyMessages: string;
    channels: string;
    timeline: { startDate: string; endDate: string };
    budget: string;
    notes: string;
    coreMessage: string;
    theoreticalBasis: string;
  }>({
    name: '',
    description: '',
    type: 'communication',
    objectives: '',
    keyMessages: '',
    channels: '',
    timeline: { startDate: '', endDate: '' },
    budget: '',
    notes: '',
    coreMessage: '',
    theoreticalBasis: '',
  });

  const [selectedParticipationLevels, setSelectedParticipationLevels] = useState<{[key: string]: string}>({});

  const {
    items: strategies,
    loading,
    error,
    selectedItem,
    showCreateModal,
    showEditModal,
    canEdit,
    canDelete,
    setShowCreateModal,
    openEditModal,
    closeModals,
    createItem,
    updateItem,
    deleteItem,
    refreshItems
  } = useCrud<Strategy>({
    fetchFn: strategyAPI.getByProject,
    createFn: strategyAPI.create,
    updateFn: strategyAPI.update,
    deleteFn: strategyAPI.delete,
    projectId: project.id
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const dataToSubmit: any = {
      project: project.id,
      name: formData.name,
      description: formData.description,
      type: formData.type
    };

    // Parse objectives
    if (formData.objectives) {
      dataToSubmit.objectives = formData.objectives.split('\n').filter(Boolean).map(obj => ({
        title: obj.trim(),
        description: '',
        completed: false
      }));
    }

    // Parse key messages
    if (formData.keyMessages) {
      dataToSubmit.keyMessages = formData.keyMessages.split('\n').filter(Boolean).map(msg => ({
        message: msg.trim(),
        priority: 'medium'
      }));
    }

    // Parse channels
    if (formData.channels) {
      dataToSubmit.channels = formData.channels.split(',').map(c => c.trim()).filter(Boolean);
    }

    // Add timeline if provided
    if (formData.timeline.startDate || formData.timeline.endDate) {
      dataToSubmit.timeline = {};
      if (formData.timeline.startDate) dataToSubmit.timeline.startDate = new Date(formData.timeline.startDate);
      if (formData.timeline.endDate) dataToSubmit.timeline.endDate = new Date(formData.timeline.endDate);
    }

    // Add budget if provided
    if (formData.budget) {
      const budgetValue = parseFloat(formData.budget);
      if (!isNaN(budgetValue)) {
        dataToSubmit.budget = {
          total: budgetValue,
          allocated: 0,
          spent: 0
        };
      }
    }

    if (formData.notes) {
      dataToSubmit.notes = formData.notes;
    }

    if (formData.coreMessage) {
      dataToSubmit.coreMessage = formData.coreMessage;
    }

    if (formData.theoreticalBasis) {
      dataToSubmit.theoreticalBasis = formData.theoreticalBasis;
    }

    // Add default participation levels from stakeholder selection
    const participationEntries = Object.entries(selectedParticipationLevels);
    if (participationEntries.length > 0) {
      dataToSubmit.participationLevels = participationEntries.map(([groupId, level]) => {
        const group = stakeholderGroups.find(g => g.id === groupId);
        return { stakeholderGroup: group?.name || groupId, level };
      });
    }

    if (selectedItem) {
      await updateItem(selectedItem._id, dataToSubmit);
    } else {
      await createItem(dataToSubmit);
    }

    resetForm();
  };

  const handleEdit = (strategy: Strategy) => {
    setFormData({
      name: strategy.name,
      description: strategy.description,
      type: strategy.type,
      objectives: strategy.objectives?.map(obj => obj.title).join('\n') || '',
      keyMessages: strategy.keyMessages?.map(msg => msg.message).join('\n') || '',
      channels: strategy.channels?.join(', ') || '',
      timeline: {
        startDate: strategy.timeline?.startDate ? new Date(strategy.timeline.startDate).toISOString().split('T')[0] : '',
        endDate: strategy.timeline?.endDate ? new Date(strategy.timeline.endDate).toISOString().split('T')[0] : ''
      },
      budget: strategy.budget?.total?.toString() || '',
      notes: strategy.notes || '',
      coreMessage: strategy.coreMessage || '',
      theoreticalBasis: strategy.theoreticalBasis || '',
    });
    openEditModal(strategy);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'communication',
      objectives: '',
      keyMessages: '',
      channels: '',
      timeline: { startDate: '', endDate: '' },
      budget: '',
      notes: '',
      coreMessage: '',
      theoreticalBasis: '',
    });
    setSelectedParticipationLevels({});
  };

  const participationLadder = [
    { level: 'informeren', description: 'Eenrichtingsverkeer: informatie verstrekken', color: '#94A3B8', icon: <MegaphoneIcon size={18} /> },
    { level: 'raadplegen', description: 'Meningen ophalen voor besluitvorming', color: '#64748B', icon: <SpeechBubbleIcon size={18} /> },
    { level: 'adviseren', description: 'Advies vragen en meewegen in besluitvorming', color: '#4B8B6E', icon: <HandshakeIcon size={18} /> },
    { level: 'coproduceren', description: 'Gezamenlijk ontwikkelen van plannen', color: '#2563EB', icon: <GroupIcon size={18} /> },
    { level: 'meebeslissen', description: 'Gedeelde beslissingsbevoegdheid', color: '#1E3A5F', icon: <BallotIcon size={18} /> }
  ];

  const stakeholderGroups = [
    { id: 'residents', name: 'Omwonenden', defaultLevel: 'adviseren' },
    { id: 'businesses', name: 'Lokale ondernemers', defaultLevel: 'coproduceren' },
    { id: 'government', name: 'Overheid', defaultLevel: 'meebeslissen' },
    { id: 'youth', name: 'Jongeren (18-35)', defaultLevel: 'raadplegen' },
    { id: 'migrants', name: 'Arbeidsmigranten', defaultLevel: 'informeren' }
  ];

  const typeOptions: { value: string; label: string; icon: React.ReactNode }[] = [
    { value: 'communication', label: 'Communicatiestrategie', icon: <MessageIcon size={18} /> },
    { value: 'engagement', label: 'Betrokkenheidsstrategie', icon: <HandshakeIcon size={18} /> },
    { value: 'crisis', label: 'Crisisstrategie', icon: <SirenIcon size={18} /> },
    { value: 'campaign', label: 'Campagnestrategie', icon: <CampaignIcon size={18} /> },
    { value: 'content', label: 'Contentstrategie', icon: <EditPenIcon size={18} /> },
    { value: 'social_media', label: 'Social Media Strategie', icon: <SmartphoneIcon size={18} /> },
    { value: 'pr', label: 'PR Strategie', icon: <NewspaperIcon size={18} /> },
    { value: 'other', label: 'Anders', icon: <ClipboardIcon size={18} /> }
  ];

  const getTypeColor = (type: Strategy['type']) => {
    switch (type) {
      case 'communication': return '#2563EB';
      case 'engagement': return '#4B8B6E';
      case 'crisis': return '#DC2626';
      case 'campaign': return '#D97706';
      case 'content': return '#7C3AED';
      case 'social_media': return '#0891B2';
      case 'pr': return '#059669';
      case 'other': return '#64748B';
      default: return '#2563EB';
    }
  };

  const getTypeLabel = (type: Strategy['type']) => {
    const option = typeOptions.find(opt => opt.value === type);
    return option?.label || 'Strategie';
  };

  const getTypeIcon = (type: Strategy['type']): React.ReactNode => {
    const option = typeOptions.find(opt => opt.value === type);
    return option?.icon || <ClipboardIcon size={18} />;
  };

  if (loading) {
    return (
      <div className={styles.strategyModule}>
        <div className={styles.loading}>Strategieën laden...</div>
      </div>
    );
  }

  // Show detail view when a strategy is selected via URL
  const viewingStrategy = subPath ? strategies.find(s => s._id === subPath) : null;
  if (subPath && viewingStrategy) {
    return (
      <div className={styles.strategyModule}>
        <StrategyDetail
          strategy={viewingStrategy as any}
          onClose={() => navigate(`/project/${project.id}/strategy`)}
          onUpdate={() => refreshItems()}
        />
      </div>
    );
  }

  return (
    <div className={styles.strategyModule}>
      <div className={styles.header}>
        <div>
          <h1>Communicatiestrategie</h1>
          <p className={styles.subtitle}>
            Ontwikkel effectieve strategieën voor stakeholdercommunicatie en participatie
          </p>
        </div>
        {canEdit && (
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
          >
            + Nieuwe Strategie
          </Button>
        )}
      </div>

      {/* Empty State */}
      {strategies.length === 0 ? (
        <div className={styles.emptyState}>
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="var(--color-content-secondary)" strokeWidth="1.5">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
          </svg>
          <h3>Nog geen strategieën</h3>
          <p>Begin met het ontwikkelen van uw eerste communicatiestrategie voor dit project.</p>
          {canEdit && (
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              + Eerste strategie maken
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Strategies Grid */}
          <div className={styles.strategiesGrid}>
            {strategies.map((strategy) => {
              const typeColor = getTypeColor(strategy.type);

              return (
                <div
                  key={strategy._id}
                  className={styles.strategyCard}
                  onClick={() => navigate(`/project/${project.id}/strategy/${strategy._id}`)}
                >
                  {/* Colored header */}
                  <div className={styles.cardColorHeader} style={{ backgroundColor: typeColor }}>
                    <span className={styles.cardHeaderIcon}>{getTypeIcon(strategy.type)}</span>
                    <span className={styles.cardHeaderType}>{getTypeLabel(strategy.type)}</span>
                  </div>

                  <div className={styles.cardBody}>
                    {/* Title */}
                    <h3 className={styles.cardTitle}>{strategy.name}</h3>

                    {/* Intro text */}
                    <p className={styles.description}>
                      {strategy.description}
                    </p>

                    {/* Theoretisch kader */}
                    {strategy.theoreticalBasis && (
                      <div className={styles.theoreticalBasis}>
                        <h4 className={styles.cardSectionLabel}>Theoretisch Kader</h4>
                        <p className={styles.cardSectionText}>{strategy.theoreticalBasis}</p>
                      </div>
                    )}

                    {/* Participatieniveaus per actor */}
                    {strategy.participationLevels && strategy.participationLevels.length > 0 && (
                      <div className={styles.participationBadges}>
                        <h4 className={styles.cardSectionLabel}>Participatieniveaus</h4>
                        <div className={styles.participationList}>
                          {strategy.participationLevels.map((pl, idx) => {
                            const ladderLevel = participationLadder.find(l => l.level === pl.level.toLowerCase());
                            return (
                              <div key={idx} className={styles.participationRow}>
                                <span className={styles.participationGroup}>{pl.stakeholderGroup}</span>
                                <span
                                  className={styles.participationLevel}
                                  style={{ backgroundColor: ladderLevel?.color || '#999' }}
                                >
                                  {pl.level.charAt(0).toUpperCase() + pl.level.slice(1)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Participation Ladder Section */}
          <section className={styles.participationSection}>
            <h2>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              Participatieladder
            </h2>

            <div className={styles.ladderContainer}>
              <div className={styles.ladder}>
                {participationLadder.map((level, index) => (
                  <div
                    key={level.level}
                    className={styles.ladderStep}
                    style={{ '--step-color': level.color } as React.CSSProperties}
                  >
                    <div className={styles.stepNumber}>{5 - index}</div>
                    <div className={styles.stepContent}>
                      <div className={styles.stepHeader}>
                        <span className={styles.stepIcon}>{level.icon}</span>
                        <h3>{level.level.charAt(0).toUpperCase() + level.level.slice(1)}</h3>
                      </div>
                      <p>{level.description}</p>
                    </div>
                    <div
                      className={styles.stepBar}
                      style={{
                        width: `${(5 - index) * 20}%`,
                        backgroundColor: level.color
                      }}
                    />
                  </div>
                ))}
              </div>

              <div className={styles.stakeholderMapping}>
                <h3>Stakeholder Participatie</h3>
                {/* Show AI-generated participation levels from strategies if available */}
                {strategies.some(s => s.participationLevels && s.participationLevels.length > 0) ? (
                  <div className={styles.stakeholderGrid}>
                    {strategies
                      .filter(s => s.participationLevels && s.participationLevels.length > 0)
                      .flatMap(s => s.participationLevels || [])
                      .reduce<{ stakeholderGroup: string; level: string; reasoning?: string }[]>((acc, pl) => {
                        // Deduplicate by stakeholderGroup
                        if (!acc.find(a => a.stakeholderGroup === pl.stakeholderGroup)) acc.push(pl);
                        return acc;
                      }, [])
                      .map((pl, idx) => {
                        const ladderLevel = participationLadder.find(l => l.level === pl.level.toLowerCase());
                        return (
                          <div key={idx} className={styles.stakeholderItem}>
                            <label>{pl.stakeholderGroup}</label>
                            <span
                              className={styles.participationBadge}
                              style={{ backgroundColor: ladderLevel?.color || '#999', color: '#fff' }}
                            >
                              {pl.level.charAt(0).toUpperCase() + pl.level.slice(1)}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div className={styles.stakeholderGrid}>
                    {stakeholderGroups.map(group => (
                      <div key={group.id} className={styles.stakeholderItem}>
                        <label>{group.name}</label>
                        <select
                          value={selectedParticipationLevels[group.id] || group.defaultLevel}
                          onChange={(e) => setSelectedParticipationLevels({
                            ...selectedParticipationLevels,
                            [group.id]: e.target.value
                          })}
                          className={styles.participationSelect}
                        >
                          {participationLadder.map(level => (
                            <option key={level.level} value={level.level}>
                              {level.level.charAt(0).toUpperCase() + level.level.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>

          <div className={styles.theoryInfo}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <div>
              <p className={styles.theoryIntro}>
                De strategiemodule is gebaseerd op bewezen theorieën en methoden voor omgevingsmanagement en participatie:
              </p>
              <ul className={styles.theoryList}>
                <li>
                  <strong>SOM-model</strong> (Strategisch OmgevingsManagement) — Biedt een raamwerk voor het managen van de relatie tussen project en omgeving op basis van belangen, posities en krachtenveldanalyse.
                </li>
                <li>
                  <strong>Mutual Gains Approach (MGA)</strong> — Onderhandelingsmethode uit Harvard: focus op belangen in plaats van posities, zoek naar gezamenlijke winst en gebruik objectieve criteria.
                  {' '}<a href="https://www.pon.harvard.edu/tag/mutual-gain/" target="_blank" rel="noopener noreferrer">Meer over Mutual Gains</a>
                </li>
                <li>
                  <strong>IAP2 Spectrum</strong> — Internationaal raamwerk voor publieke participatie, van informeren tot meebeslissen. Basis voor de participatieladder in deze module.
                  {' '}<a href="https://www.iap2.org/page/pillars" target="_blank" rel="noopener noreferrer">Meer over IAP2</a>
                </li>
                <li>
                  <strong>CASI-model</strong> (Communicatie Activatie Strategie Instrument) — Communicatiestrategisch instrument waarmee je via een gestructureerde analyse bepaalt welke communicatie-interventies het meest effectief zijn om gewenst gedrag te beïnvloeden.
                  {' '}<a href="https://www.communicatierijk.nl/vakkennis/gedrag-casi-en-campagnes/casi" target="_blank" rel="noopener noreferrer">Meer over CASI</a>
                </li>
                <li>
                  <strong>COM-B model</strong> (Capability, Opportunity, Motivation → Behaviour) — Gedragsmodel dat helpt begrijpen waarom stakeholders zich op een bepaalde manier gedragen en hoe communicatie daarop kan inspelen.
                  {' '}<a href="https://implementationscience.biomedcentral.com/articles/10.1186/1748-5908-6-42" target="_blank" rel="noopener noreferrer">Meer over COM-B</a>
                </li>
              </ul>
            </div>
          </div>
        </>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          closeModals();
          resetForm();
        }}
        title={selectedItem ? 'Strategie Bewerken' : 'Nieuwe Strategie'}
        footer={
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <Button variant="outline" onClick={() => {
              closeModals();
              resetForm();
            }}>
              Annuleren
            </Button>
            <Button variant="primary" onClick={handleSubmit}>
              {selectedItem ? 'Opslaan' : 'Toevoegen'}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Basisinformatie</h3>

            <div className={styles.formGroup}>
              <label htmlFor="name" className={styles.label}>
                Naam <span className={styles.required}>*</span>
              </label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className={styles.input}
                placeholder="Bijv. Q1 2024 Communicatiestrategie"
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="type" className={styles.label}>
                  Type <span className={styles.required}>*</span>
                </label>
                <select
                  id="type"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as Strategy['type'] })}
                  className={styles.select}
                >
                  {typeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="budget" className={styles.label}>Budget (€)</label>
                <input
                  id="budget"
                  type="number"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  className={styles.input}
                  placeholder="50000"
                  min="0"
                  step="100"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="description" className={styles.label}>
                Beschrijving <span className={styles.required}>*</span>
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={3}
                className={styles.textarea}
                placeholder="Beschrijf de strategie en de belangrijkste uitgangspunten..."
              />
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Strategie Inhoud</h3>

            <div className={styles.formGroup}>
              <label htmlFor="coreMessage" className={styles.label}>
                Kernboodschap (Story of the Day)
              </label>
              <textarea
                id="coreMessage"
                value={formData.coreMessage}
                onChange={(e) => setFormData({ ...formData, coreMessage: e.target.value })}
                rows={3}
                className={styles.textarea}
                placeholder="De centrale boodschap die alle communicatie verbindt..."
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="theoreticalBasis" className={styles.label}>
                Theoretisch Kader
              </label>
              <textarea
                id="theoreticalBasis"
                value={formData.theoreticalBasis}
                onChange={(e) => setFormData({ ...formData, theoreticalBasis: e.target.value })}
                rows={3}
                className={styles.textarea}
                placeholder="Bijv. SOM/MGA-model, IAP2 Spectrum, Arnstein's Ladder..."
              />
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Participatieniveaus</h3>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
              Wijs een participatieniveau toe per stakeholdergroep
            </p>
            {stakeholderGroups.map(group => (
              <div key={group.id} className={styles.formRow} style={{ marginBottom: '0.5rem', alignItems: 'center' }}>
                <div className={styles.formGroup} style={{ flex: 1 }}>
                  <label className={styles.label}>{group.name}</label>
                </div>
                <div className={styles.formGroup} style={{ flex: 1 }}>
                  <select
                    value={selectedParticipationLevels[group.id] || group.defaultLevel}
                    onChange={(e) => setSelectedParticipationLevels({
                      ...selectedParticipationLevels,
                      [group.id]: e.target.value
                    })}
                    className={styles.select}
                  >
                    {participationLadder.map(level => (
                      <option key={level.level} value={level.level}>
                        {level.level.charAt(0).toUpperCase() + level.level.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Doelstellingen & Boodschappen</h3>

            <div className={styles.formGroup}>
              <label htmlFor="objectives" className={styles.label}>
                Doelstellingen
                <span className={styles.helpText}>Één per regel</span>
              </label>
              <textarea
                id="objectives"
                value={formData.objectives}
                onChange={(e) => setFormData({ ...formData, objectives: e.target.value })}
                rows={4}
                className={styles.textarea}
                placeholder="Verhoog bekendheid met 25%&#10;Bereik 80% van de doelgroep&#10;Verminder weerstand met 50%"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="keyMessages" className={styles.label}>
                Kernboodschappen
                <span className={styles.helpText}>Één per regel</span>
              </label>
              <textarea
                id="keyMessages"
                value={formData.keyMessages}
                onChange={(e) => setFormData({ ...formData, keyMessages: e.target.value })}
                rows={4}
                className={styles.textarea}
                placeholder="Samen bouwen we aan een duurzame toekomst&#10;Uw mening telt mee&#10;Transparantie staat voorop"
              />
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Planning & Kanalen</h3>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="startDate" className={styles.label}>Startdatum</label>
                <input
                  id="startDate"
                  type="date"
                  value={formData.timeline.startDate}
                  onChange={(e) => setFormData({
                    ...formData,
                    timeline: { ...formData.timeline, startDate: e.target.value }
                  })}
                  className={styles.input}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="endDate" className={styles.label}>Einddatum</label>
                <input
                  id="endDate"
                  type="date"
                  value={formData.timeline.endDate}
                  onChange={(e) => setFormData({
                    ...formData,
                    timeline: { ...formData.timeline, endDate: e.target.value }
                  })}
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="channels" className={styles.label}>
                Communicatiekanalen
                <span className={styles.helpText}>Gescheiden door komma's</span>
              </label>
              <input
                id="channels"
                type="text"
                value={formData.channels}
                onChange={(e) => setFormData({ ...formData, channels: e.target.value })}
                className={styles.input}
                placeholder="Website, Nieuwsbrief, Social media, Informatiebijeenkomst"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="notes" className={styles.label}>Notities</label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className={styles.textarea}
                placeholder="Aanvullende opmerkingen of aandachtspunten..."
              />
            </div>
          </div>
        </form>
      </Modal>

    </div>
  );
};